import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Typography,
  Input,
  Autocomplete,
  Snackbar,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {
  CertificateCreate,
  CertificateUpdate,
  MaterialGrade,
  Surface,
  Lieferant,
  Country,
  MaterialNorm,
  Certificate,
  CertificateMetalValueCreate,
  IssuedZeugnisRef,
  CertificateMechanicalValueCreate,
  BusinessUnit,
  CertificateExtractionResult,
} from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import CertificateMetalValuesForm from './CertificateMetalValuesForm';
import CertificateMechanicalValuesForm from './CertificateMechanicalValuesForm';
import { useAuth } from '../services/AuthContext';
import { apiErrorMessage } from '../utils/apiError';
import CountrySelect from './CountrySelect';

interface CertificateFormProps {
  certificate?: Certificate;
  onSuccess: () => void;
  onCancel: () => void;
}

const CertificateForm: React.FC<CertificateFormProps> = ({ certificate, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    certificate_number: certificate?.certificate_number || '',
    lieferant_id: certificate?.lieferant_id || '',
    certificate_date: certificate ? dayjs(certificate.certificate_date) : dayjs(),
    material_grade_id: certificate?.material_grade_id || '',
    surface_id: certificate?.surface_id || '',
    country_id: certificate?.country_id || '',
    melt_country_id: certificate?.melt_country_id || '',
    heat_number: certificate?.heat_number || '',
    melt_works: certificate?.melt_works || '',
    material_norm_id: certificate?.material_norm_id || '',
    notes: certificate?.notes || '',
    business_unit: certificate?.business_unit || ('KALOTTE' as BusinessUnit),
  });
  const [metalValues, setMetalValues] = useState<CertificateMetalValueCreate[]>(
    certificate?.metal_values?.map((v) => ({
      metal_name: v.metal_name,
      measured_percentage: v.measured_percentage,
    })) || []
  );
  const [mechanicalValues, setMechanicalValues] = useState<CertificateMechanicalValueCreate[]>(
    certificate?.mechanical_values?.map((v) => ({
      property_name: v.property_name,
      measured_value: v.measured_value,
      unit: v.unit,
    })) || []
  );
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [issuedPrompt, setIssuedPrompt] = useState<IssuedZeugnisRef[] | null>(null);
  const [pendingUpdate, setPendingUpdate] = useState<CertificateUpdate | null>(null);
  const [materialGrades, setMaterialGrades] = useState<MaterialGrade[]>([]);
  const [surfaces, setSurfaces] = useState<Surface[]>([]);
  const [lieferanten, setLieferanten] = useState<Lieferant[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [materialNorms, setMaterialNorms] = useState<MaterialNorm[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [numberTaken, setNumberTaken] = useState(false);
  const [takenCertificateId, setTakenCertificateId] = useState<number | null>(null);
  const [loadingMasterData, setLoadingMasterData] = useState(true);

  // AI state
  const [aiFilled, setAiFilled] = useState<Set<string>>(new Set());
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSnackbar, setAiSnackbar] = useState<{
    message: string;
    severity: 'success' | 'warning' | 'error';
  } | null>(null);

  const aiSx = (fieldName: string) =>
    aiFilled.has(fieldName)
      ? { '& .MuiInputBase-root': { backgroundColor: 'warning.light', opacity: 0.85 } }
      : undefined;

  const clearAiMark = (fieldName: string) => {
    setAiFilled((prev) => {
      if (!prev.has(fieldName)) return prev;
      const next = new Set(prev);
      next.delete(fieldName);
      return next;
    });
  };

  const isEdit = !!certificate;

  useEffect(() => {
    const number = formData.certificate_number.trim();
    const ownNumber = (certificate?.certificate_number ?? '').trim();
    if (!number || (isEdit && number === ownNumber)) {
      setNumberTaken(false);
      setTakenCertificateId(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      apiService
        .certificateNumberTaken(number, isEdit ? certificate?.id : undefined)
        .then((result) => {
          if (cancelled) return;
          setNumberTaken(result.exists);
          setTakenCertificateId(result.certificate_id);
        })
        .catch(() => {
          if (cancelled) return;
          setNumberTaken(false);
          setTakenCertificateId(null);
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [formData.certificate_number, isEdit, certificate?.id, certificate?.certificate_number]);
  const selectedGrade = materialGrades.find(
    (grade) => grade.id === Number(formData.material_grade_id)
  );
  const noSurfaceNeeded = ['ALU', 'HSF_PP', 'HSF_PE'].includes(selectedGrade?.material_type ?? '');
  const selectedGradeCompositions = selectedGrade?.metal_compositions ?? [];
  const selectedGradeMechanicalProperties = selectedGrade?.mechanical_properties ?? [];

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    try {
      const [gradesData, surfacesData, lieferantenData, countriesData, normsData] =
        await Promise.all([
          apiService.getMaterialGrades(),
          apiService.getSurfaces(),
          apiService.getLieferanten(),
          apiService.getCountries(),
          apiService.getMaterialNorms(),
        ]);
      setMaterialGrades(gradesData);
      setSurfaces(surfacesData);
      setLieferanten(lieferantenData);
      setCountries(countriesData);
      setMaterialNorms(normsData);
    } catch (err) {
      setError('Fehler beim Laden der Stammdaten');
      console.error('Error loading master data:', err);
    } finally {
      setLoadingMasterData(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'material_grade_id') {
        const grade = materialGrades.find((g) => g.id === Number(value));
        if (grade && grade.material_type !== 'EDELSTAHL') {
          updated.surface_id = '';
        }
        updated.material_norm_id = '';
      }
      return updated;
    });
    setError(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Nur PDF-Dateien sind erlaubt');
        return;
      }
      setPdfFile(file);
      setError(null);
    }
  };

  const handleAiExtract = async () => {
    if (!pdfFile) return;
    setAiLoading(true);
    setError(null);
    try {
      const result: CertificateExtractionResult = await apiService.extractFromPdf(pdfFile);
      const newAiFilled = new Set<string>();

      setFormData((prev) => {
        const next = { ...prev };
        if (result.certificate_number) {
          next.certificate_number = result.certificate_number;
          newAiFilled.add('certificate_number');
        }
        if (result.certificate_date) {
          next.certificate_date = dayjs(result.certificate_date);
          newAiFilled.add('certificate_date');
        }
        if (result.business_unit) {
          next.business_unit = result.business_unit;
          newAiFilled.add('business_unit');
        }
        if (result.notes) {
          next.notes = result.notes;
          newAiFilled.add('notes');
        }
        if (result.lieferant.value !== null) {
          next.lieferant_id = result.lieferant.value;
          newAiFilled.add('lieferant_id');
        }
        if (result.material_grade.value !== null) {
          next.material_grade_id = result.material_grade.value;
          newAiFilled.add('material_grade_id');
        }
        if (result.surface.value !== null) {
          next.surface_id = result.surface.value;
          newAiFilled.add('surface_id');
        }
        if (result.country.value !== null) {
          next.country_id = result.country.value;
          newAiFilled.add('country_id');
        }
        if (result.material_norm.value !== null) {
          next.material_norm_id = result.material_norm.value;
          newAiFilled.add('material_norm_id');
        }
        return next;
      });

      if (result.metal_values.length > 0) {
        setMetalValues(
          result.metal_values.map((mv) => ({
            metal_name: mv.metal_name,
            measured_percentage: mv.measured_percentage,
          }))
        );
        newAiFilled.add('metal_values');
      }

      if (result.mechanical_values.length > 0) {
        setMechanicalValues(
          result.mechanical_values.map((mv) => ({
            property_name: mv.property_name,
            measured_value: mv.measured_value,
            unit: mv.unit ?? '',
          }))
        );
        newAiFilled.add('mechanical_values');
      }

      setAiFilled(newAiFilled);

      if (result.extraction_warnings.length > 0) {
        setAiSnackbar({
          message: `Extraktion abgeschlossen mit Hinweisen: ${result.extraction_warnings.join('; ')}`,
          severity: 'warning',
        });
      } else {
        setAiSnackbar({
          message: 'KI-Extraktion erfolgreich. Bitte Felder überprüfen.',
          severity: 'success',
        });
      }
    } catch (err: any) {
      setAiSnackbar({
        message: apiErrorMessage(
          err,
          'KI-Extraktion fehlgeschlagen — bitte Felder manuell ausfüllen.'
        ),
        severity: 'error',
      });
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent | null, incomplete = false) => {
    event?.preventDefault();
    setLoading(true);
    setError(null);

    const number = formData.certificate_number.trim();
    try {
      if (number && !(isEdit && number === (certificate?.certificate_number ?? '').trim())) {
        const taken = await apiService.certificateNumberTaken(
          number,
          isEdit ? certificate?.id : undefined
        );
        if (taken.exists) {
          setNumberTaken(true);
          setTakenCertificateId(taken.certificate_id);
          return;
        }
      }

      const certificateData = {
        certificate_number: number,
        lieferant_id: Number(formData.lieferant_id),
        certificate_date: formData.certificate_date.format('YYYY-MM-DD'),
        material_grade_id: Number(formData.material_grade_id),
        surface_id: formData.surface_id ? Number(formData.surface_id) : null,
        country_id: formData.country_id ? Number(formData.country_id) : null,
        melt_country_id: formData.melt_country_id ? Number(formData.melt_country_id) : null,
        heat_number: formData.heat_number.trim(),
        blocked: incomplete,
        melt_works: formData.melt_works.trim(),
        material_norm_id: formData.material_norm_id ? Number(formData.material_norm_id) : null,
        notes: formData.notes || null,
        metal_values: metalValues.length > 0 ? metalValues : undefined,
        mechanical_values: mechanicalValues.length > 0 ? mechanicalValues : undefined,
        business_unit: formData.business_unit,
      };

      if (isEdit) {
        // Check if metal values have changed
        const originalMetalValues =
          certificate.metal_values?.map((v) => ({
            metal_name: v.metal_name,
            measured_percentage: v.measured_percentage,
          })) || [];
        const metalValuesChanged =
          JSON.stringify(metalValues) !== JSON.stringify(originalMetalValues);

        // Check if mechanical values have changed
        const originalMechanicalValues =
          certificate.mechanical_values?.map((v) => ({
            property_name: v.property_name,
            measured_value: v.measured_value,
            unit: v.unit,
          })) || [];
        const mechanicalValuesChanged =
          JSON.stringify(mechanicalValues) !== JSON.stringify(originalMechanicalValues);

        const updateData: CertificateUpdate = {
          certificate_number:
            certificateData.certificate_number !== certificate.certificate_number
              ? certificateData.certificate_number
              : undefined,
          lieferant_id:
            certificateData.lieferant_id !== certificate.lieferant_id
              ? certificateData.lieferant_id
              : undefined,
          certificate_date:
            certificateData.certificate_date !==
            dayjs(certificate.certificate_date).format('YYYY-MM-DD')
              ? certificateData.certificate_date
              : undefined,
          material_grade_id:
            certificateData.material_grade_id !== certificate.material_grade_id
              ? certificateData.material_grade_id
              : undefined,
          surface_id:
            certificateData.surface_id !== (certificate.surface_id ?? null)
              ? certificateData.surface_id
              : undefined,
          country_id:
            certificateData.country_id !== (certificate.country_id ?? null)
              ? certificateData.country_id
              : undefined,
          melt_country_id:
            certificateData.melt_country_id !== (certificate.melt_country_id ?? null)
              ? certificateData.melt_country_id
              : undefined,
          heat_number:
            certificateData.heat_number !== (certificate.heat_number ?? '')
              ? certificateData.heat_number
              : undefined,
          melt_works:
            certificateData.melt_works !== (certificate.melt_works ?? '')
              ? certificateData.melt_works
              : undefined,
          material_norm_id:
            certificateData.material_norm_id !== (certificate.material_norm_id ?? null)
              ? certificateData.material_norm_id
              : undefined,
          notes:
            (certificateData.notes ?? '') !== (certificate.notes ?? '')
              ? certificateData.notes
              : undefined,
          metal_values: metalValuesChanged ? metalValues : undefined,
          mechanical_values: mechanicalValuesChanged ? mechanicalValues : undefined,
          business_unit:
            formData.business_unit !== certificate.business_unit
              ? formData.business_unit
              : undefined,
          blocked: incomplete ? true : certificate.blocked ? false : undefined,
        };
        if (incomplete && !formData.heat_number.trim()) {
          updateData.heat_number = '';
        }
        if (incomplete && !formData.melt_country_id) {
          updateData.melt_country_id = null;
        }

        const changed = Object.entries(updateData).some(
          ([key, value]) => key !== 'blocked' && value !== undefined
        );
        const blockedChanged =
          updateData.blocked !== undefined && updateData.blocked !== !!certificate.blocked;
        if (!changed && !blockedChanged) {
          onSuccess();
          return;
        }
        if (changed) {
          const issued = await apiService.listIssuedZeugnisse(certificate.id);
          if (issued.length > 0) {
            setPendingUpdate(updateData);
            setIssuedPrompt(issued);
            return;
          }
        }
        await apiService.updateCertificate(certificate.id, updateData, pdfFile || undefined);
      } else {
        await apiService.createCertificate(
          certificateData as CertificateCreate,
          pdfFile || undefined
        );
      }

      onSuccess();
    } catch (err: any) {
      setError(
        apiErrorMessage(
          err,
          `Fehler beim ${isEdit ? 'Aktualisieren' : 'Erstellen'} des Zertifikats`
        )
      );
      console.error('Error saving certificate:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loadingMasterData) {
    return <LoadingSpinner message="Stammdaten werden geladen..." />;
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            required
            fullWidth
            label="Zertifikatsnummer"
            value={formData.certificate_number}
            onChange={(e) => {
              handleInputChange('certificate_number', e.target.value);
              clearAiMark('certificate_number');
              setNumberTaken(false);
              setTakenCertificateId(null);
            }}
            disabled={loading}
            error={numberTaken}
            helperText={
              numberTaken ? (
                <>
                  Diese Zertifikatsnummer ist bereits vergeben.
                  {takenCertificateId != null && (
                    <>
                      {' '}
                      <Link component={RouterLink} to={`/certificates/${takenCertificateId}`}>
                        Öffnen
                      </Link>
                    </>
                  )}
                </>
              ) : undefined
            }
            sx={aiSx('certificate_number')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            required
            fullWidth
            label="Schmelznummer"
            value={formData.heat_number}
            onChange={(e) => handleInputChange('heat_number', e.target.value)}
            disabled={loading}
            helperText="Heat No. vom Werkszeugnis, z. B. 0477534"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Schmelzwerk"
            value={formData.melt_works}
            onChange={(e) => handleInputChange('melt_works', e.target.value)}
            disabled={loading}
            helperText="Optional — Werk, in dem geschmolzen wurde"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            options={lieferanten}
            getOptionLabel={(option) => `${option.name} (${option.short_name})`}
            value={lieferanten.find((l) => l.id === Number(formData.lieferant_id)) || null}
            onChange={(_, newValue) => {
              handleInputChange('lieferant_id', newValue?.id || '');
              clearAiMark('lieferant_id');
            }}
            disabled={loading}
            renderInput={(params) => (
              <TextField {...params} required label="Lieferant" sx={aiSx('lieferant_id')} />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DatePicker
            label="Zertifikatsdatum"
            value={formData.certificate_date}
            onChange={(newValue) => {
              handleInputChange('certificate_date', newValue);
              clearAiMark('certificate_date');
            }}
            disabled={loading}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                sx: aiSx('certificate_date'),
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            options={materialGrades}
            getOptionLabel={(option) =>
              option.description ? `${option.name} - ${option.description}` : option.name
            }
            value={materialGrades.find((g) => g.id === Number(formData.material_grade_id)) || null}
            onChange={(_, newValue) => {
              handleInputChange('material_grade_id', newValue?.id || '');
              clearAiMark('material_grade_id');
            }}
            disabled={loading}
            renderInput={(params) => (
              <TextField {...params} label="Materialgüte" required sx={aiSx('material_grade_id')} />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Grid>
        {!noSurfaceNeeded && (
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              options={surfaces}
              getOptionLabel={(option) =>
                option.description ? `${option.name} - ${option.description}` : option.name
              }
              value={surfaces.find((s) => s.id === Number(formData.surface_id)) || null}
              onChange={(_, newValue) => {
                handleInputChange('surface_id', newValue?.id || '');
                clearAiMark('surface_id');
              }}
              disabled={loading}
              renderInput={(params) => (
                <TextField {...params} label="Oberfläche" required sx={aiSx('surface_id')} />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <CountrySelect
            countries={countries}
            value={countries.find((c) => c.id === Number(formData.melt_country_id)) || null}
            onChange={(country) => handleInputChange('melt_country_id', country?.id || '')}
            required
            disabled={loading}
            showMeltPourCode
            helperText="Nur Land aus der Liste, z. B. Italien (IT) · Y386"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <CountrySelect
            countries={countries}
            value={countries.find((c) => c.id === Number(formData.country_id)) || null}
            onChange={(country) => {
              handleInputChange('country_id', country?.id || '');
              clearAiMark('country_id');
            }}
            label="Ursprungsland"
            disabled={loading}
            helperText="Nur Land aus der Liste, z. B. Italien (IT)"
            sx={aiSx('country_id')}
          />
        </Grid>
        {formData.material_grade_id && (
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              options={materialNorms.filter((norm) => {
                const gradeType = selectedGrade?.material_type;
                if (gradeType === 'EDELSTAHL') return norm.material_type === 'EDELSTAHL';
                if (gradeType === 'ALU' || gradeType === 'HSF_PP' || gradeType === 'HSF_PE')
                  return norm.material_type === 'ALU';
                return true;
              })}
              getOptionLabel={(option) => option.name}
              value={materialNorms.find((n) => n.id === Number(formData.material_norm_id)) || null}
              onChange={(_, newValue) => {
                handleInputChange('material_norm_id', newValue?.id || '');
                clearAiMark('material_norm_id');
              }}
              disabled={loading}
              renderInput={(params) => (
                <TextField {...params} label="Werkstoffnorm" sx={aiSx('material_norm_id')} />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>
        )}
        {user?.role === 'KALOTTE' && (
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth required>
              <InputLabel>Bereich</InputLabel>
              <Select
                value={formData.business_unit}
                onChange={(e) => {
                  handleInputChange('business_unit', e.target.value as BusinessUnit);
                  clearAiMark('business_unit');
                }}
                label="Bereich"
                disabled={loading}
                sx={aiSx('business_unit')}
              >
                <MenuItem value="KALOTTE">Kalotte</MenuItem>
                <MenuItem value="RUBY">Ruby</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} md={8}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              PDF-Datei {isEdit ? '(optional - nur bei Änderung)' : '(optional)'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" component="label" disabled={loading}>
                PDF auswählen
                <Input
                  type="file"
                  inputProps={{ accept: 'application/pdf' }}
                  onChange={handleFileChange}
                  sx={{ display: 'none' }}
                />
              </Button>
              <Button
                type="button"
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleAiExtract}
                disabled={!pdfFile || aiLoading || loading}
              >
                {aiLoading ? 'KI extrahiert...' : 'KI Auto-Fill'}
              </Button>
              {pdfFile && (
                <Typography variant="caption" color="textSecondary">
                  Ausgewählte Datei: {pdfFile.name}
                </Typography>
              )}
              {isEdit && certificate?.pdf_filename && !pdfFile && (
                <Typography variant="caption" color="textSecondary">
                  Aktuelle Datei: {certificate.pdf_filename}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notizen"
            value={formData.notes}
            onChange={(e) => {
              handleInputChange('notes', e.target.value);
              clearAiMark('notes');
            }}
            multiline
            rows={2}
            disabled={loading}
            sx={aiSx('notes')}
          />
        </Grid>
      </Grid>

      {/* Metal Values Section */}
      {formData.material_grade_id && (
        <Box sx={{ mt: 2, mb: 1 }}>
          <Box
            sx={
              aiFilled.has('metal_values')
                ? { backgroundColor: 'warning.light', opacity: 0.85, borderRadius: 1, p: 1 }
                : undefined
            }
          >
            <CertificateMetalValuesForm
              metalValues={metalValues}
              onChange={(newValues) => {
                setMetalValues(newValues);
                clearAiMark('metal_values');
              }}
              materialGradeCompositions={selectedGradeCompositions}
              materialType={selectedGrade?.material_type}
              disabled={loading}
            />
          </Box>
        </Box>
      )}

      {/* Mechanical Values Section */}
      {formData.material_grade_id && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Box
            sx={
              aiFilled.has('mechanical_values')
                ? { backgroundColor: 'warning.light', opacity: 0.85, borderRadius: 1, p: 1 }
                : undefined
            }
          >
            <CertificateMechanicalValuesForm
              mechanicalValues={mechanicalValues}
              onChange={(newValues) => {
                setMechanicalValues(newValues);
                clearAiMark('mechanical_values');
              }}
              materialGradeMechanicalProperties={selectedGradeMechanicalProperties}
              disabled={loading}
            />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          mt: 2,
          pt: 1.5,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          zIndex: 2,
        }}
      >
        <Button type="button" onClick={onCancel} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          type="button"
          variant="outlined"
          disabled={
            loading ||
            numberTaken ||
            !formData.certificate_number.trim() ||
            !formData.lieferant_id ||
            !formData.certificate_date ||
            !formData.material_grade_id
          }
          onClick={() => void handleSubmit(null, true)}
        >
          Unvollständig speichern
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={
            loading ||
            numberTaken ||
            !formData.certificate_number.trim() ||
            !formData.heat_number.trim() ||
            !formData.lieferant_id ||
            !formData.melt_country_id ||
            !formData.certificate_date ||
            !formData.material_grade_id ||
            (!noSurfaceNeeded && !formData.surface_id)
          }
        >
          {loading ? 'Speichern...' : isEdit ? 'Aktualisieren' : 'Erstellen'}
        </Button>
      </Box>

      <Dialog
        open={issuedPrompt !== null}
        onClose={() => setIssuedPrompt(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Zeugnis bereits ausgestellt</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Für diese Chargen liegt bereits ein Zeugnis vor:{' '}
            {issuedPrompt
              ?.map((row) => `${row.charge_number} (${row.zeugnis_number})`)
              .join(', ')}
            . Die ausgestellten Dokumente bleiben, wie sie sind. Das nächste Erstellen schreibt
            dafür ein neues Zeugnis mit den geänderten Werten.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssuedPrompt(null)}>Abbrechen</Button>
          <Button
            variant="contained"
            disabled={loading || pendingUpdate === null}
            onClick={() => {
              if (!certificate || pendingUpdate === null) return;
              const updateData = pendingUpdate;
              setIssuedPrompt(null);
              setLoading(true);
              setError(null);
              void apiService
                .updateCertificate(certificate.id, updateData, pdfFile || undefined)
                .then(() => {
                  setPendingUpdate(null);
                  onSuccess();
                })
                .catch((err: unknown) => {
                  setError(apiErrorMessage(err, 'Fehler beim Aktualisieren des Zertifikats'));
                })
                .finally(() => setLoading(false));
            }}
          >
            Trotzdem speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={aiSnackbar !== null}
        autoHideDuration={6000}
        onClose={() => setAiSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {aiSnackbar !== null ? (
          <Alert
            onClose={() => setAiSnackbar(null)}
            severity={aiSnackbar.severity}
            sx={{ width: '100%' }}
          >
            {aiSnackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default CertificateForm;
